import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Percent, DollarSign, X, Check, Lock, Tag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Line-level discount editor — used by POS cart rows.
 * Encapsulates: type toggle (% / amount), value, reason picker, and the
 * Manager-PIN approval modal when the cashier exceeds the per-line cap.
 *
 * Props:
 *   - item: { unit_price, qty, discount_type, discount_value, discount_reason_id, discount_reason_label, _discount_manager_pin }
 *   - reasons: [{ reason_id, label_ar, label_en, active }]
 *   - policy: { enabled, require_reason, cashier_max_percent, cashier_max_amount, manager_pin_set, ... }
 *   - onApply(patch): merges the patch into the cart item
 *   - onClose(): close popover
 */
export default function LineDiscountEditor({ item, reasons, policy, onApply, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [type, setType] = useState(item.discount_type && item.discount_type !== "none" ? item.discount_type : "percent");
  const [value, setValue] = useState(item.discount_value || 0);
  const [reasonId, setReasonId] = useState(item.discount_reason_id || "");
  const [pin, setPin] = useState("");
  const [needsPin, setNeedsPin] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationErr, setValidationErr] = useState("");

  const lineSubtotal = (item.unit_price || 0) * (item.qty || 1);
  const previewAmount = (() => {
    if (!value || value <= 0) return 0;
    if (type === "percent") return Math.min(value, 100) / 100 * lineSubtotal;
    return Math.min(value, lineSubtotal);
  })();
  const previewLineTotal = Math.max(0, lineSubtotal - previewAmount);

  // Live preflight against the policy — debounced async call to /validate.
  useEffect(() => {
    if (!value || value <= 0) {
      setNeedsPin(false);
      setValidationErr("");
      return undefined;
    }
    let cancelled = false;
    const tm = setTimeout(() => {
      setValidating(true);
      api.post("/pos/validate-line-discount", {
        unit_price: item.unit_price, qty: item.qty,
        discount_type: type, discount_value: parseFloat(value) || 0,
        has_reason: !!reasonId,
        manager_pin: pin || undefined,
      }).then((r) => {
        if (cancelled) return;
        if (r.data?.ok) { setNeedsPin(false); setValidationErr(""); }
        else { setNeedsPin(!!r.data?.requires_pin); setValidationErr(isAr ? r.data?.message_ar : r.data?.message_en); }
      }).catch(() => { /* swallow — apply surfaces server errors */ })
        .finally(() => { if (!cancelled) setValidating(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(tm); };
  }, [type, value, reasonId, pin, item.unit_price, item.qty, isAr]);

  const apply = async () => {
    if (policy?.require_reason && !reasonId && value > 0) {
      toast.error(isAr ? "اختر سبب الخصم" : "Pick a reason");
      return;
    }
    if (needsPin && !pin) {
      toast.error(isAr ? "أدخل PIN المدير للموافقة" : "Enter manager PIN to proceed");
      return;
    }
    const r = reasons.find((x) => x.reason_id === reasonId);
    onApply({
      discount_type: value > 0 ? type : "none",
      discount_value: parseFloat(value) || 0,
      discount_reason_id: value > 0 ? reasonId || null : null,
      discount_reason_label: value > 0 && r ? (isAr ? r.label_ar : r.label_en) : null,
      _discount_manager_pin: needsPin && pin ? pin : undefined,
    });
    onClose();
  };

  const clear = () => {
    onApply({
      discount_type: "none", discount_value: 0,
      discount_reason_id: null, discount_reason_label: null,
      _discount_manager_pin: undefined,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="line-discount-modal" onMouseDown={(e) => onBackdropMouseDown(e, onClose)}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5"
           onMouseDown={onModalPanelMouseDown} dir={isAr ? "rtl" : "ltr"}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-stone-800 flex items-center gap-2">
            <Tag size={18} className="text-amber-600"/>
            {isAr ? "خصم على هذا البند" : "Line Discount"}
          </h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={18}/></button>
        </div>

        {/* Type toggle + value */}
        <div className="grid grid-cols-5 gap-2 mb-3">
          <button onClick={() => setType("percent")} data-testid="ld-type-percent"
                  className={`col-span-1 py-2 rounded-lg border-2 flex items-center justify-center transition ${
                    type === "percent" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-stone-200 text-stone-500"
                  }`}><Percent size={16}/></button>
          <button onClick={() => setType("amount")} data-testid="ld-type-amount"
                  className={`col-span-1 py-2 rounded-lg border-2 flex items-center justify-center transition ${
                    type === "amount" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-stone-200 text-stone-500"
                  }`}><DollarSign size={16}/></button>
          <input type="number" min="0" step="0.5" autoFocus
                 value={value} onChange={(e) => setValue(e.target.value)}
                 data-testid="ld-value-input"
                 placeholder="0" inputMode="decimal"
                 className="col-span-3 border-2 border-stone-200 rounded-lg px-3 py-2 text-base num-display font-bold text-center focus:border-amber-400 outline-none"/>
        </div>

        {/* Reason picker */}
        <div className="mb-3">
          <label className="text-xs text-stone-500 mb-1 block flex items-center gap-1">
            {isAr ? "سبب الخصم" : "Reason"}
            {policy?.require_reason && <span className="text-rose-500">*</span>}
          </label>
          <select value={reasonId} onChange={(e) => setReasonId(e.target.value)}
                  data-testid="ld-reason-select"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">{isAr ? "— لا يوجد —" : "— None —"}</option>
            {reasons.filter((r) => r.active !== false).map((r) => (
              <option key={r.reason_id} value={r.reason_id}>{isAr ? r.label_ar : r.label_en}</option>
            ))}
          </select>
        </div>

        {/* Manager PIN slot — only when policy demands it */}
        {needsPin && (
          <div className="mb-3 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="ld-pin-section">
            <div className="text-xs text-rose-800 mb-2 flex items-start gap-2">
              <Lock size={14} className="shrink-0 mt-0.5"/>
              <span>{validationErr}</span>
            </div>
            <input type="password" inputMode="numeric"
                   placeholder={isAr ? "PIN المدير (4-8 أرقام)" : "Manager PIN (4-8 digits)"}
                   value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                   data-testid="ld-manager-pin"
                   className="w-full border-2 border-rose-200 rounded-lg px-3 py-2 text-sm font-mono text-center tracking-widest focus:border-rose-400 outline-none"/>
          </div>
        )}

        {/* Live preview */}
        <div className="bg-stone-50 rounded-lg p-3 text-sm space-y-1 border border-stone-200">
          <div className="flex justify-between text-stone-600">
            <span>{isAr ? "سعر البند" : "Line subtotal"}</span>
            <span className="num-display">{lineSubtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-rose-600">
            <span>{isAr ? "قيمة الخصم" : "Discount"}</span>
            <span className="num-display">-{previewAmount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-emerald-700 border-t border-stone-200 pt-1">
            <span>{isAr ? "الصافي" : "Line total"}</span>
            <span className="num-display">{previewLineTotal.toFixed(2)}</span>
          </div>
        </div>

        {validating && <div className="text-[10px] text-stone-400 mt-1 text-center">{isAr ? "تحقق..." : "Validating..."}</div>}

        <div className="flex justify-between gap-2 mt-5">
          {(item.discount_type && item.discount_type !== "none") || value > 0 ? (
            <button onClick={clear} data-testid="ld-clear"
                    className="px-3 py-2 rounded-lg text-sm text-stone-600 hover:bg-stone-100">
              {isAr ? "إزالة الخصم" : "Remove discount"}
            </button>
          ) : <span/>}
          <button onClick={apply} data-testid="ld-apply"
                  className="btn-gold px-5 py-2 rounded-lg text-sm flex items-center gap-1.5">
            <Check size={14}/>{isAr ? "تطبيق" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
